import * as React from 'react';

import { useForkedRef, useId, useRect } from '~shared/hooks';
import { makeId } from '~shared/utils';
import { getOwnerDocument } from '~shared/utils/dom';
import { wrapEvent } from '~shared/utils/react';

import { Portal } from '../Portal';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import {
  TooltipEvent,
  TooltipStates,
  context,
  state,
  subscribe,
  transition,
} from './state';
import {
  PRect,
  TooltipContentProps,
  TooltipParams,
  TooltipPopupProps,
  TooltipProps,
  TriggerParams,
} from './types';
import { centered, getStyles, positionDefault } from './utils';

/**
 * useTooltip
 */
export function useTooltip<T extends HTMLElement>({
  id: idProp,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onFocus,
  onBlur,
  onKeyDown,
  onMouseDown,
  ref: forwardedRef,
  isDisabled,
  DEBUG_STYLE,
}: {
  ref?: React.Ref<any>;
  isDisabled?: boolean;
  DEBUG_STYLE?: boolean;
} & React.HTMLAttributes<T> = {}): [TriggerParams, TooltipParams, boolean] {
  const id = String(useId(idProp));

  const [isVisible, setIsVisible] = React.useState(
    DEBUG_STYLE
      ? true
      : id === null
      ? false
      : context.id === id && state === TooltipStates.VISIBLE
  );

  // hopefully they always pass a ref if they ever pass one
  const ownRef = React.useRef<HTMLDivElement | null>(null);

  const ref = useForkedRef(forwardedRef as any, ownRef); // TODO: Fix in utils
  const triggerRect = useRect(ownRef, isVisible);

  React.useEffect(() => {
    return subscribe(() => {
      if (
        !isDisabled &&
        context.id === id &&
        (state === TooltipStates.VISIBLE ||
          state === TooltipStates.LEAVING_VISIBLE)
      ) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    });
  }, [isDisabled, id]);

  React.useEffect(() => {
    const ownerDocument = getOwnerDocument(ownRef.current) || document;

    function listener(event: KeyboardEvent) {
      if (
        (event.key === 'Escape' || event.key === 'Esc') &&
        state === TooltipStates.VISIBLE
      ) {
        transition(TooltipEvent.SELECT_WITH_KEYBOARD);
      }
    }

    ownerDocument.addEventListener('keydown', listener);

    return () => ownerDocument.removeEventListener('keydown', listener);
  }, []);

  function handleMouseEnter() {
    transition(TooltipEvent.MOUSE_ENTER, { id });
  }

  function handleMouseMove() {
    transition(TooltipEvent.MOUSE_MOVE, { id });
  }

  function handleFocus() {
    transition(TooltipEvent.FOCUS, { id });
  }

  function handleMouseLeave() {
    transition(TooltipEvent.MOUSE_LEAVE);
  }

  function handleBlur() {
    // Allow quick click from one tool to another
    if (context.id !== id) return;
    transition(TooltipEvent.BLUR);
  }

  function handleMouseDown() {
    // Allow quick click from one tool to another
    if (context.id !== id) return;
    transition(TooltipEvent.MOUSE_DOWN);
  }

  function handleKeyDown(event: React.KeyboardEvent<T>) {
    if (event.key === 'Enter' || event.key === ' ') {
      transition(TooltipEvent.SELECT_WITH_KEYBOARD);
    }
  }

  const trigger: TriggerParams = {
    /**
     * The element that triggers the tooltip references the tooltip element with
     * `aria-describedby`
     * @see https://www.w3.org/TR/wai-aria-practices-1.2/#tooltip
     */
    'aria-describedby': isVisible ? makeId('tooltip', id) : undefined,
    'data-tooltip-trigger': '',
    ref,
    onMouseEnter: wrapEvent(onMouseEnter, handleMouseEnter),
    onMouseMove: wrapEvent(onMouseMove, handleMouseMove),
    onFocus: wrapEvent(onFocus, handleFocus),
    onBlur: wrapEvent(onBlur, handleBlur),
    onMouseLeave: wrapEvent(onMouseLeave, handleMouseLeave),
    onKeyDown: wrapEvent(onKeyDown, handleKeyDown),
    onMouseDown: wrapEvent(onMouseDown, handleMouseDown),
  };

  const tooltip: TooltipParams = {
    id,
    triggerRect,
    isVisible,
  };

  return [trigger, tooltip, isVisible];
}

////////////////////////////////////////////////////////////////////////////////

/**
 * When the user's mouse or focus rests on an element, a non-interactive popup
 * is displayed near it.
 *
 * Quick definitions:
 *
 * - "on rest" or "rested on": describes when the element receives mouse hover
 *   after a short delay (and hopefully soon, touch longpress).
 * - "activation": describes a mouse click, keyboard enter, or keyboard space.
 *
 * Only one tooltip can be visible at a time, so we use a global state chart to
 * describe the various states and transitions between states that are
 * possible. With all the timeouts involved with tooltips it's important to
 * "make impossible states impossible" with a state machine.
 *
 * It's also okay to use these module globals because you don't server render
 * tooltips. None of the state is changed outside of user events.
 *
 * There are a few features that are important to understand:
 *
 * 1. Tooltips don't show up until the user has rested on one, we don't
 *    want tooltips popping up as you move your mouse around the page.
 * 2. Once any tooltip becomes visible, other tooltips nearby should skip
 *    resting and display immediately.
 * 3. Tooltips stick around for a little bit after blur/mouseleave.
 *
 * @todo Research longpress tooltips on Android, iOS
 *       - Probably want to position it by default above, since your thumb is
 *         below and would cover it
 *       - I'm thinking after longpress, display the tooltip and cancel any
 *         click events. Then on touchend, so they can read it display the
 *         tooltip for a little while longer in case their hand was
 *         obstructing the tooltip.
 *
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices-1.2/#tooltip
 */
export const Tooltip = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & TooltipProps
>(function Tooltip(
  {
    children,
    label,
    id,
    isDisabled,
    DEBUG_STYLE,
    ...props
  }: React.HTMLAttributes<HTMLDivElement> & TooltipProps,
  forwardedRef
) {
  const child = React.Children.only(children) as React.ReactElement & {
    ref?: React.Ref<any>;
  };

  /**
   * We need to pass some properties from the child into useTooltip to make sure
   * users can maintain control over the trigger's ref and events.
   */
  const [trigger, tooltip] = useTooltip({
    id,
    onMouseEnter: child.props.onMouseEnter,
    onMouseMove: child.props.onMouseMove,
    onMouseLeave: child.props.onMouseLeave,
    onFocus: child.props.onFocus,
    onBlur: child.props.onBlur,
    onKeyDown: child.props.onKeyDown,
    onMouseDown: child.props.onMouseDown,
    ref: child.ref,
    isDisabled,
    DEBUG_STYLE,
  });

  return (
    <>
      {React.cloneElement(child, trigger as any)}
      <TooltipPopup
        ref={forwardedRef}
        label={label}
        {...tooltip}
        {...props}
        position={centered}
      />
    </>
  );
});

export default Tooltip;

/**
 * TooltipPopup
 */
export const TooltipPopup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & TooltipPopupProps
>(function TooltipPopup(
  {
    // could use children but we want to encourage simple strings
    label,
    isVisible,
    id,
    ...props
  }: React.HTMLAttributes<HTMLDivElement> & TooltipPopupProps,
  forwardRef
) {
  return isVisible ? (
    <Portal>
      <TooltipContent
        ref={forwardRef}
        label={label}
        isVisible={isVisible}
        {...props}
        id={makeId('tooltip', String(id))}
      />
    </Portal>
  ) : null;
});

/**
 * TooltipContent
 *
 * We need a separate component so that useRect works inside the portal.
 */
const TooltipContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & TooltipContentProps
>(function TooltipContent(
  {
    'aria-label': realAriaLabel,
    id,
    isVisible,
    label,
    position = positionDefault,
    style,
    triggerRect,
    ...props
  }: React.HTMLAttributes<HTMLDivElement> & TooltipContentProps,
  forwardedRef
) {
  /**
   * The element that serves as the tooltip container has role tooltip.
   * When an app passes an `aria-label`, we actually want to implement
   * `role="tooltip"` on a visually hidden element inside of the trigger.
   * In these cases we want the screen reader user to know both the content in
   * the tooltip, but also the content in the badge. For screen reader users,
   * the only content announced to them is whatever is in the tooltip.
   *
   * @see https://www.w3.org/TR/wai-aria-practices-1.2/#tooltip
   */
  const hasAriaLabel = realAriaLabel != null;

  const ownRef = React.useRef(null);
  const ref = useForkedRef(forwardedRef, ownRef);
  const tooltipRect = useRect(ownRef, isVisible);

  return (
    <>
      <div
        className="absolute z-100 border-black-95 w-0 h-0"
        style={{
          left: triggerRect ? triggerRect.left - 6 + triggerRect.width / 2 : 0,
          top: triggerRect ? triggerRect.bottom + window.scrollY : 0,
          borderTop: '0',
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderBottom: '6px solid',
        }}
      />
      <div
        className="absolute z-100 max-w-56 py-1 px-3 text-white leading-6 bg-black-95 rounded-1 pointer-events-none"
        role={hasAriaLabel ? undefined : 'tooltip'}
        {...props}
        ref={ref}
        id={hasAriaLabel ? undefined : id}
        style={{
          ...style,
          ...getStyles(position, triggerRect as PRect, tooltipRect as PRect),
        }}
      >
        {label}
      </div>
      {hasAriaLabel && (
        <VisuallyHidden role="tooltip" id={id}>
          {realAriaLabel}
        </VisuallyHidden>
      )}
    </>
  );
});
